import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, QueryList, ViewChild, ViewChildren, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { SelectModule as DropdownModule } from 'primeng/select';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CardModule } from 'primeng/card';
import { OneToManyComponent } from '../one-to-many/one-to-many.component';
import { PaginationMobileVersionComponent } from '../pagination-mobile-version/pagination-mobile-version.component';
import { UtcToLocalPipe } from '../../pipes/utc-to-local.pipe';
import { RelativeDateLabelPipe } from '../../pipes/relative-date-label.pipe';
import { categoryDropdown, subCategoryDropdown } from '../../consts';
import { ErrorHandlingService, SubCategoryDropdownModel } from '../../../core';
import { NoteService } from '../../../services';
import { NoteEditComponent } from '../../../components/note/note-edit/note-edit.component';
import { catchError, finalize } from 'rxjs';

@Component({
  selector: 'app-notes-card',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DropdownModule,
    ConfirmDialogModule,
    CardModule,
    OneToManyComponent,
    PaginationMobileVersionComponent,
    UtcToLocalPipe,
    RelativeDateLabelPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './notes-card.component.html',
  styleUrls: ['./notes-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotesCardComponent {
  noteService = inject(NoteService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private errorHandlingService = inject(ErrorHandlingService);
  private destroyRef = inject(DestroyRef);

  formGroup = input.required<FormGroup>();
  entityId = input<string | null | undefined>(null);
  entityType = input<string>('Quote');
  parentIdKey = input<string>('quoteID');
  foreignKeyName = input<string>('entityID');
  defaultSort = input<any>({ createdOn: 'DESC' });
  cols = input<any[]>([]);
  enablePagination = input<boolean>(false);
  pageSize = input<number>(10);
  rowsPerPageOptions = input<number[]>([10, 20, 50]);
  showHeader = input<boolean>(true);

  noteSaved = output<void>();

  noteComponentType = NoteEditComponent;
  noteFields = ['entityID'];

  noteForm = new FormGroup({
    category: new FormControl(null),
    subCategory: new FormControl({ value: null, disabled: true }),
    content: new FormControl(null, Validators.required),
  });

  categories = categoryDropdown();
  subCategories = subCategoryDropdown();
  filteredSubCategories = signal<SubCategoryDropdownModel[]>([]);

  notesList = signal<any[]>([]);
  notesTotal = signal<number>(0);
  expandedNotes = signal<Set<string>>(new Set());
  officialDateNotes = signal<Set<string>>(new Set());
  @ViewChild('noteFormEl') noteFormEl?: ElementRef;

  showNewNoteForm = signal(false);
  /** When set, the top form is editing an existing note; save uses PATCH with this id. */
  editingNoteId = signal<string | null>(null);
  savingNote = signal(false);
  deletingNoteId = signal<string | null>(null);

  notesSkip = signal<number>(0);
  notesTake = signal<number>(10);

  paginatedNotes = computed(() => {
    const list = this.notesList();
    if (!this.enablePagination()) {
      return list;
    }

    // When backend already returns paged data, render as-is.
    if (this.notesTotal() > list.length) {
      return list;
    }

    if (list.length <= this.pageSize()) {
      return list;
    }

    const skip = this.notesSkip();
    const take = this.notesTake();
    return list.slice(skip, skip + take);
  });

  constructor() {
    effect(() => {
      const take = this.pageSize();
      this.notesTake.set(take);
    });
  }

  @ViewChildren(OneToManyComponent) oneToManyComponents!: QueryList<OneToManyComponent>;

  resolveEntityId(): string | null {
    const directEntityId = this.entityId();
    if (directEntityId) {
      return String(directEntityId);
    }

    const form = this.formGroup();
    const parentValue = form?.get(this.parentIdKey())?.value;
    if (parentValue !== null && parentValue !== undefined && parentValue !== '') {
      return String(parentValue);
    }

    return null;
  }

  onDataNote(event: any) {
    const form = this.formGroup();
    const list = event?.list || [];
    const total = typeof event?.total === 'number' ? event.total : list.length;
    const control = form?.get('notes');
    if (control) {
      control.patchValue(list);
    }

    const sortedNotes = list.sort((a: any, b: any) => {
      if (!a.createdOn && !b.createdOn) return 0;
      if (!a.createdOn) return 1;
      if (!b.createdOn) return -1;
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    });
    this.notesList.set(sortedNotes);
    this.notesTotal.set(total);
    if (this.notesSkip() >= total) {
      this.notesSkip.set(0);
    }
    this.expandedNotes.set(new Set());
    this.officialDateNotes.set(new Set());
  }

  selectSubCategories(category: any, options?: { preserveSubCategory?: string | null }) {
    const subCategoryControl = this.noteForm.controls['subCategory'];
    if (category) {
      const filtered = this.subCategories.filter((s) => s.filterField === category);
      this.filteredSubCategories.set(filtered);
      subCategoryControl.enable({ emitEvent: false });
      const subVal = options && 'preserveSubCategory' in options ? (options.preserveSubCategory ?? null) : null;
      subCategoryControl.patchValue(subVal as never, { emitEvent: false });
    } else {
      this.filteredSubCategories.set([]);
      subCategoryControl.reset(null, { emitEvent: false });
      subCategoryControl.disable({ emitEvent: false });
    }
  }

  showNewNote() {
    if (!this.resolveEntityId()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save Required',
        detail: 'Save the record first before adding a note.',
      });
      return;
    }

    this.editingNoteId.set(null);
    this.showNewNoteForm.set(true);
    this.noteForm.reset();
    this.noteForm.controls['subCategory'].disable({ emitEvent: false });
    this.filteredSubCategories.set([]);
  }

  startEditNote(note: { noteID: string | number; category?: string | null; subCategory?: string | null; content?: string | null }) {
    const id = note.noteID != null ? String(note.noteID) : '';
    if (!id) {
      return;
    }
    this.showNewNoteForm.set(false);
    this.editingNoteId.set(id);
    this.noteForm.reset();
    const category = this.resolveOptionValue(this.categories, note.category);
    const subCategory = this.resolveOptionValue(category ? this.subCategories.filter((item) => item.filterField === category) : this.subCategories, note.subCategory);
    this.selectSubCategories(category, { preserveSubCategory: subCategory });
    this.noteForm.patchValue(
      {
        category,
        subCategory,
        content: note.content ?? null,
      } as never,
      { emitEvent: false },
    );
    this.noteForm.markAsPristine();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    if (!this.showNewNoteForm() && !this.editingNoteId()) return;
    const target = event.target as HTMLElement;
    if (this.noteFormEl?.nativeElement?.contains(target)) return;
    if (target.closest('[data-pc-name="select"],[data-pc-name="popover"],[data-pc-name="datepicker"]')) return;
    this.cancelNoteForm();
  }

  /** Prevent textarea blur from auto-saving when the user clicks Cancel. */
  onCancelNoteMousedown(event: MouseEvent) {
    event.preventDefault();
  }

  cancelNoteForm() {
    this.showNewNoteForm.set(false);
    this.editingNoteId.set(null);
    this.noteForm.reset();
    this.noteForm.controls['subCategory'].disable({ emitEvent: false });
    this.filteredSubCategories.set([]);
  }

  saveNote() {
    const resolvedEntityId = this.resolveEntityId();
    const editingId = this.editingNoteId();

    if (!editingId && !resolvedEntityId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save Required',
        detail: 'Save the record first before adding a note.',
      });
      return;
    }

    if (this.savingNote() || !this.noteForm.valid) {
      return;
    }

    this.savingNote.set(true);

    if (editingId) {
      const patchFields: Record<string, unknown> = {
        ...this.noteForm.value,
        entityID: resolvedEntityId ?? undefined,
        entityType: this.entityType(),
      };
      this.noteService
        .patch(editingId, patchFields)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error) => {
            this.savingNote.set(false);
            return this.errorHandlingService.handleError(error, 'Error updating note.');
          }),
        )
        .subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Note updated successfully',
          });
          this.afterNoteSaved();
        });
      return;
    }

    const noteData = {
      ...this.noteForm.value,
      entityID: resolvedEntityId,
      entityType: this.entityType(),
    };

    this.noteService
      .save(noteData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.savingNote.set(false);
          return this.errorHandlingService.handleError(error, 'Error saving note.');
        }),
      )
      .subscribe(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Note saved successfully',
        });
        this.afterNoteSaved();
      });
  }

  private afterNoteSaved() {
    this.noteForm.reset();
    this.showNewNoteForm.set(false);
    this.editingNoteId.set(null);
    this.filteredSubCategories.set([]);
    this.savingNote.set(false);
    this.noteForm.controls['subCategory'].disable({ emitEvent: false });

    const noteComp = this.oneToManyComponents?.find((c) => c.id === 'noteID');
    if (noteComp) {
      noteComp.load({ first: 0, rows: noteComp.paginateQuery.take });
    }
    this.noteSaved.emit();
  }

  onNoteContentKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.saveNote();
    }
  }

  /** Auto-save on blur for new notes only; skip when focus moves to the form actions (Cancel/Save). */
  onNoteContentBlur(event?: FocusEvent) {
    if (this.editingNoteId() != null) {
      return;
    }
    const relatedTarget = event?.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('.note-form-actions')) {
      return;
    }
    if (this.noteForm.valid && this.noteForm.dirty && !this.savingNote()) {
      this.saveNote();
    }
  }

  confirmDeleteNote(event: Event, note: { noteID?: string | number | null }) {
    if (this.savingNote() || this.deletingNoteId()) {
      return;
    }
    const id = note.noteID != null ? String(note.noteID) : '';
    if (!id) {
      return;
    }
    this.confirmationService.confirm({
      target: (event?.target as EventTarget) ?? undefined,
      message: 'Do you want to delete this note?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'quote-confirm-outline-danger',
      rejectButtonStyleClass: 'quote-confirm-text-primary',
      accept: () => this.deleteNote(note),
      reject: () => {},
    });
  }

  deleteNote(note: { noteID?: string | number | null }) {
    const id = note.noteID != null ? String(note.noteID) : '';
    if (!id) {
      return;
    }
    this.deletingNoteId.set(id);
    this.noteService
      .delete(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.deletingNoteId.set(null)),
        catchError((error) => {
          return this.errorHandlingService.handleError(error, 'Error deleting note.');
        }),
      )
      .subscribe(() => {
        if (this.editingNoteId() === id) {
          this.cancelNoteForm();
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Note deleted successfully',
        });
        const noteComp = this.oneToManyComponents?.find((c) => c.id === 'noteID');
        if (noteComp) {
          noteComp.load({ first: 0, rows: noteComp.paginateQuery.take });
        }
        this.noteSaved.emit();
      });
  }

  isDeletingNote(note: { noteID?: unknown }): boolean {
    const id = note.noteID != null ? String(note.noteID) : '';
    return id !== '' && this.deletingNoteId() === id;
  }

  onNotesPageChange(event: { first: number; rows: number }): void {
    this.notesSkip.set(event.first);
    this.notesTake.set(event.rows);
    const noteComp = this.oneToManyComponents?.find((c) => c.id === 'noteID');
    if (noteComp) {
      noteComp.load({ first: event.first, rows: event.rows });
    }
    this.expandedNotes.set(new Set());
    this.officialDateNotes.set(new Set());
  }

  getCategoryDescription(categoryId: string): string {
    const category = this.categories.find((c) => c.id === categoryId);
    return category?.description || categoryId || '--';
  }

  getSubCategoryDescription(subCategoryId: string): string {
    const subCategory = this.subCategories.find((s) => s.id === subCategoryId);
    return subCategory?.description || subCategoryId || '--';
  }

  isEditingThisNote(note: { noteID: unknown }): boolean {
    const id = this.editingNoteId();
    if (id == null) {
      return false;
    }
    return String(note.noteID) === id;
  }

  getNoteKey(note: any): string {
    return String(note?.noteID || `${note?.createdOn || ''}-${note?.content || ''}`);
  }

  toggleNoteDetails(note: any): void {
    const key = this.getNoteKey(note);
    if (!key) return;
    const next = new Set(this.expandedNotes());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.expandedNotes.set(next);
  }

  isNoteDetailsExpanded(note: any): boolean {
    return this.expandedNotes().has(this.getNoteKey(note));
  }

  toggleNoteDateDisplay(note: any, event?: Event): void {
    event?.stopPropagation();
    const key = this.getNoteKey(note);
    if (!key) return;
    const next = new Set(this.officialDateNotes());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.officialDateNotes.set(next);
  }

  isOfficialNoteDate(note: any): boolean {
    return this.officialDateNotes().has(this.getNoteKey(note));
  }

  getNoteAuthorDisplay(note: any): string {
    const nestedUser = note?.createdByUser || note?.user || note?.createdByInfo || note?.author || null;

    const fullNameFromNested = this.buildName(nestedUser?.firstName, nestedUser?.middleName, nestedUser?.lastName);
    if (fullNameFromNested) {
      return fullNameFromNested;
    }

    const directFullName = this.buildName(
      note?.createdByFirstName || note?.firstName,
      note?.createdByMiddleName || note?.middleName,
      note?.createdByLastName || note?.lastName,
    );
    if (directFullName) {
      return directFullName;
    }

    const candidates = [note?.createdByName, note?.authorName, note?.userName, nestedUser?.name, note?.createdBy, typeof note?.user === 'string' ? note.user : null]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    const nonEmailCandidate = candidates.find((value) => !value.includes('@'));
    return nonEmailCandidate || candidates[0] || '';
  }

  private buildName(first?: string, middle?: string, last?: string): string {
    return [first, middle, last]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ');
  }

  private resolveOptionValue(options: Array<{ id: string; description: string }>, selectedValue?: string | null): string | null {
    const value = String(selectedValue || '').trim();
    if (!value) {
      return null;
    }

    const match = options.find((item) => item.id === value || item.description === value);
    return match?.id ?? value;
  }
}
